import { ref, provide, inject } from "vue";
import type { InjectionKey } from "vue";

import { getBookingScanCounts } from "@/api/check-in/scan-counts";
import CheckInModule from "@/store/modules/check-in";
import { LogService } from "@/services/logging";
import type { IBookingCheckedIn } from "@/types/check-in";

const FLUSH_MS = 50;

type CheckInCountsApi = {
  countFor: (id: string, reference: string) => IBookingCheckedIn | undefined;
  requestForId: (id: string, reference: string) => boolean;
  clear: () => boolean;
};

const KEY: InjectionKey<CheckInCountsApi> = Symbol("orders-check-in");

/**
 * Provide a batched check-in count reader to descendant cells.
 *
 * Cells register a booking id with its order reference, the queue is
 * flushed as one aggregation over the ticket index. A check-in made on this
 * terminal, kept by order reference, wins until the index catches up, the
 * way every ES-backed screen lags.
 *
 * @returns {CheckInCountsApi} Count reader and queue handle, also provided
 * for child cells through `useOrdersCheckInInject`.
 */
export function useOrdersCheckInProvide(): CheckInCountsApi {
  const fetched = ref<Record<string, IBookingCheckedIn>>({});

  const pendingIds = new Set<string>();
  // The index answers by booking id, the local counts are by reference.
  const referenceById = new Map<string, string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let generation = 0;

  /**
   * Get the count of one order: the one recorded on this terminal first,
   * then the index.
   *
   * @param {string} id - Booking id the index counts by.
   * @param {string} reference - Reference of the order.
   * @returns {IBookingCheckedIn | undefined} The count, if any is known.
   */
  function countFor(
    id: string,
    reference: string
  ): IBookingCheckedIn | undefined {
    return CheckInModule.orderCounts[reference] ?? fetched.value[id];
  }

  /**
   * Queue a booking id for the next batch. An order checked in on this
   * terminal is still read, so its local count can retire once the index
   * agrees.
   *
   * @param {string} id - Booking id to count.
   * @param {string} reference - Reference of the order.
   * @returns {boolean} Sentinel.
   */
  function requestForId(id: string, reference: string): boolean {
    if (reference) {
      referenceById.set(id, reference);
    }

    if (id in fetched.value) {
      return true;
    }

    if (pendingIds.has(id)) {
      return true;
    }

    pendingIds.add(id);

    if (timer) {
      return true;
    }

    timer = setTimeout(flush, FLUSH_MS);

    return true;
  }

  /**
   * Drain the queue into one aggregation and fold the answer into the cache.
   *
   * @returns {Promise<void>} Resolves once the cache holds the answer.
   */
  async function flush(): Promise<void> {
    timer = null;

    const ids = Array.from(pendingIds);

    pendingIds.clear();

    if (!ids.length) {
      return;
    }

    // A clear() while the request is in flight bumps the generation, and the
    // answer is dropped instead of bringing the old rows back.
    const myGeneration = generation;

    // Seed zeros so an order with no access ticket is not asked for again.
    const next = { ...fetched.value };

    for (const id of ids) {
      next[id] = next[id] ?? { checkedIn: 0, total: 0 };
    }

    try {
      const counts = await getBookingScanCounts(ids.map(Number));

      for (const count of counts) {
        next[String(count.bookingId)] = {
          checkedIn: count.checkedIn,
          total: count.total,
        };

        // The local count only covers the indexing lag. Once the index has
        // caught up it is dropped, so a check-in made on another terminal
        // is not hidden behind ours.
        const reference = referenceById.get(String(count.bookingId));
        const local = reference ? CheckInModule.orderCounts[reference] : null;

        if (reference && local && count.checkedIn >= local.checkedIn) {
          CheckInModule.clearOrderCount(reference);
        }
      }
    } catch (error) {
      // The seeded zeros stay, so a failure shows an empty count instead of
      // retrying on every render.
      LogService.info("Check-in: could not read the counts of the orders", {
        error,
      });
    }

    if (myGeneration !== generation) {
      return;
    }

    fetched.value = next;
  }

  /**
   * Drop the cache and any queue in flight. The table calls this when it
   * fetches a new set of rows.
   *
   * @returns {boolean} Sentinel.
   */
  function clear(): boolean {
    fetched.value = {};
    pendingIds.clear();
    referenceById.clear();
    generation += 1;

    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    return true;
  }

  const api: CheckInCountsApi = { countFor, requestForId, clear };

  provide(KEY, api);

  return api;
}

/**
 * Consume the counts provided by an ancestor `useOrdersCheckInProvide`.
 *
 * @returns {CheckInCountsApi} The cache and queue handle of the provider.
 */
export function useOrdersCheckInInject(): CheckInCountsApi {
  const api = inject(KEY);

  if (!api) {
    throw new Error(
      "useOrdersCheckInInject must be called inside an useOrdersCheckInProvide tree"
    );
  }

  return api;
}
